import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import * as approvalTypeApi from "../../api/approval/type";
import { getMeta } from "../../api/approval/meta";
import FormContentManager from "../../components/common/form/FormContentManager";
import TableCell from "../../components/common/ui/TableCell";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";

export default function Request() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeProjectId, dns } = useParams();
  const selectedType = location.state?.selectedType || null;
  const [isEditMode, setIsEditMode] = useState(Boolean(dns));


  const editorRef = useRef(null);

  const projectId = routeProjectId || localStorage.getItem("projectId") || "";
  const projectCode =
    localStorage.getItem("projectCode") ||
    localStorage.getItem("project_code") ||
    "";
  const pid = projectId || projectCode;

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    title: selectedType?.title || "",
    content: selectedType?.content || "",
    document_type: selectedType?.id || "",
    doc_type: selectedType?.code || selectedType?.doc_type ||"",
    is_urgent: false,
  });

  // ✅ 결재 문서 유형 목록 불러오기 (approvalTypeApi 사용)
  const loadList = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const res = await approvalTypeApi.getTypeList(pid);
      setTypes(res || []);
      console.log("결재 문서 유형 목록:", res);
      // 새 문서 작성 시 기본값 세팅
      if (!selectedType) {
        const general =
          res.find((t) => t.name === "일반" || t.title === "일반") || res[0];
        setForm((prev) => ({
          ...prev,
          document_type: general?.id ? String(general.id) : "",
          doc_type: general?.code || general?.doc_type || "",
          title: general?.title || "",
          content: general?.content || "",
        }));
      }
    } catch (err) {
      console.error("결재 문서 유형 목록 불러오기 실패:", err);
      setTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, [pid]);

  // -----------------------------------------------------------
  //   2) 수정모드 → 기존 문서 가져오기
  // -----------------------------------------------------------
  useEffect(() => {
    if (!dns) return; // 신규 작성이면 스킵
    setIsEditMode(true);

    async function loadDetail() {
      try {
        const data = await getMeta(dns);  // ⭐ 기존 문서 상세 API
        console.log("기존 결재 상세:", data);

        setForm({
          title: data?.title || "",
          content: data?.meta.content || "",
          document_type: data?.meta.document_type?.id || "",
          doc_type: data?.meta.doc_type || "",
          is_urgent: data?.meta.is_urgent || false,
        });

        // TinyMCE 값 주입
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.setContent(
              data?.meta?.content || data?.content || ""
            );
          }
        }, 200);
      } catch (err) {
        console.error("결재 문서 상세 불러오기 실패:", err);
      }
    }

    loadDetail();
  }, [dns]);
  console.log(form);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "document_type") {
      const valStr = String(value);
      const selected = types.find((t) => String(t.id) === valStr);
      setForm((prev) => ({
        ...prev,
        document_type: valStr,
        doc_type: selected?.code || prev.doc_type,
        title: selected?.title || prev.title,
        content: selected?.content || prev.content,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const getBody = () => {
    const html = editorRef.current?.getContent() || "";

    return {
      layout: null,
      values: {
        project: projectId,
        doc_type: form.doc_type, // ★ 반드시 전달
        document_type: form.document_type,
        is_urgent: form.is_urgent,
        title: form.title,
        content: html,

        /* ★ approvalMeta 구조에 맞게 meta 내부 구조도 전달 */
        meta: {
          title: form.title,
          content: html,
          doc_type: form.doc_type,
          is_urgent: form.is_urgent,
          document_type: form.document_type,
        },
      },
    };
  };
  return (
    <FormContentManager
      params={{ module: "approval", project: pid }}
      docName="결재 상신"
      docDescribe="결재 문서를 작성하고 결재선을 지정해 상신합니다."
      getBody={getBody}
      onSaved={() => navigate(`/project/${pid}/approval`)}
    >
      <table className="w-full text-sm border">
        <colgroup>
          <col style={{ width: "120px" }} />
          <col style={{ width: "70%" }} />
        </colgroup>
        <tbody className="divide-y">
          <tr className="divide-x">
            <TableCell
              label="문서 유형"
              editing
              input={
                <select
                  name="document_type"
                  value={form.document_type}
                  disabled={isEditMode}
                  onChange={handleChange}
                  className="input-base"
                >
                  {types.length === 0 ? (
                    <option value="">유형 없음</option>
                  ) : (
                    types.map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        {t.name || t.title}
                      </option>
                    ))
                  )}
                </select>
              }
            />
            <TableCell
              label="긴급 여부"
              editing
              input={
                <input
                  type="checkbox"
                  name="is_urgent"
                  checked={form.is_urgent}
                  onChange={handleChange}
                  className="w-4 h-4 text-red-500"
                />
              }
            />
          </tr>

          <tr className="divide-x">
            <TableCell
              label="제목"
              editing
              colSpan={3}
              input={
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  className="input-base"
                />
              }
            />
          </tr>

          <tr className="divide-x align-top">
            <TableCell
              label="내용"
              colSpan={3}
              editing
              input={
                <TinyMCEeditor
                  initialValue={form.content}
                  editorRef={editorRef}
                />
              }
              valueBg="bg-white align-top"
            />
          </tr>
        </tbody>
      </table>
    </FormContentManager>
  );
}
